'use client';

import { useEffect, useState } from 'react';
import { BRAND } from "@/lib/brand";
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, collection, addDoc, setDoc, query, where, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Loader2, CheckCircle, XCircle, Shield, Image, FileText, Users, FolderOpen } from 'lucide-react';
import { Suspense } from 'react';

type AuthStep = 'loading' | 'authorize' | 'generating' | 'success' | 'error';

function FigmaAuthCallback() {
  const searchParams = useSearchParams();
  const { user, loading: authLoading, organizations } = useAuth();
  const [step, setStep] = useState<AuthStep>('loading');
  const [message, setMessage] = useState('Loading...');
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{
    email: string;
    orgName: string;
    orgSlug: string;
    orgId: string;
    userId: string;
  } | null>(null);

  useEffect(() => {
    async function checkAuth() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      try {
        if (!user) {
          setStep('error');
          setMessage('You must be signed in to connect the Figma plugin.');
          return;
        }

        // Get user's organizations from AuthContext
        if (organizations.length === 0) {
          setStep('error');
          setMessage('No organization found. Please create or join an organization first.');
          return;
        }

        // Use selected org or default to first organization
        const orgId = selectedOrgId || organizations[0].id;
        const orgData = organizations.find(o => o.id === orgId) || organizations[0];

        setUserInfo({
          email: user.email || 'Unknown',
          orgName: orgData.name || orgData.slug,
          orgSlug: orgData.slug || orgId,
          orgId,
          userId: user.uid
        });

        setStep('authorize');

      } catch (error) {
        console.error('Error during auth check:', error);
        setStep('error');
        setMessage('An error occurred. Please try again.');
      }
    }

    checkAuth();
  }, [user, authLoading, organizations, selectedOrgId]);

  async function handleAuthorize() {
    setStep('generating');
    setMessage('Creating secure connection...');

    try {
      if (!user) {
        setStep('error');
        setMessage('Authentication required.');
        return;
      }

      const orgId = userInfo!.orgId;

      // Pre-generate a document ID so we can compute the hash before creation
      const apiKeysCollection = collection(db, 'orgs', orgId, 'apiKeys');
      const newDocRef = doc(apiKeysCollection);
      const docId = newDocRef.id;

      // Generate the actual API key using the pre-generated document ID
      const generatedKey = `dc_${orgId}_${docId}`;

      // Hash the API key using Web Crypto API (same as server-side SHA-256)
      const encoder = new TextEncoder();
      const data = encoder.encode(generatedKey);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const keyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Now create the document with the hash included using setDoc with the pre-generated ref
      await setDoc(newDocRef, {
        name: 'Figma Plugin',
        type: 'user',
        userId: user.uid,
        orgId: orgId,
        orgSlug: userInfo!.orgSlug,
        orgName: userInfo!.orgName,
        app: 'figma',
        scopes: ['updates:write', 'uploads:write', 'teams:read', 'projects:read'],
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        isActive: true,
        lastUsedAt: null,
        revokedAt: null,
        keyHash: keyHash, // Include hash in initial creation
      });

      setApiKey(generatedKey);

      // Copy to clipboard automatically
      try {
        await navigator.clipboard.writeText(generatedKey);
        setStep('success');
        setMessage('Connection created! API key copied to clipboard.');
      } catch (error) {
        setStep('success');
        setMessage('Connection created! Copy the API key below.');
      }

    } catch (error) {
      console.error('Error during authorization:', error);
      setStep('error');
      setMessage('Failed to create connection. Please try again.');
    }
  }

  function handleDeny() {
    window.close();
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white p-6">
      <div className="max-w-md w-full">
        {step === 'loading' && (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-500">{message}</p>
          </div>
        )}

        {step === 'authorize' && userInfo && (
          <div className="space-y-8">
            {/* Header */}
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-gray-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-1">Authorize Figma Plugin</h1>
              <p className="text-sm text-gray-500">
                Connect Figma to {BRAND.name}
              </p>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                {userInfo.email[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userInfo.email}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  @{userInfo.orgSlug}
                </p>
              </div>
            </div>

            {/* Organization Selector */}
            {organizations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Select Organization
                </label>
                <select
                  value={userInfo.orgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name || org.slug} (@{org.slug})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Permissions */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                This will allow Figma Plugin to:
              </h3>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-sm text-gray-600">
                  <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Create and post updates to your feed</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600">
                  <Image className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Upload images and attachments</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600">
                  <Users className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>View your teams</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-gray-600">
                  <FolderOpen className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>View your projects</span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleDeny}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAuthorize}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Authorize
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center">
              You can revoke access anytime from Settings → API Keys
            </p>
          </div>
        )}

        {step === 'generating' && (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-sm font-medium text-gray-900 mb-1">{message}</p>
            <p className="text-xs text-gray-500">This will only take a moment...</p>
          </div>
        )}

        {step === 'success' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Connection Successful
              </h2>
              <p className="text-sm text-gray-500">{message}</p>
            </div>

            {apiKey && (
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg font-mono text-xs break-all text-gray-700">
                  {apiKey}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(apiKey);
                    setMessage('Copied! Now paste it into the Figma plugin.');
                  }}
                  className="w-full px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Copy API Key Again
                </button>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                onClick={() => window.close()}
                className="w-full px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close this window
              </button>
              <p className="text-xs text-gray-400 text-center">
                Return to Figma and paste the API key to connect.
              </p>
              {userInfo && (
                <Link
                  href={`/${userInfo.orgSlug}`}
                  className="block text-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Or go to your workspace →
                </Link>
              )}
            </div>
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Authorization Failed
              </h2>
              <p className="text-sm text-gray-500">{message}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Try Again
              </button>
              <Link
                href="/auth"
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors text-center"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FigmaAuthPage() {
  return (
    <Suspense fallback={
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    }>
      <FigmaAuthCallback />
    </Suspense>
  );
}
